import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, orderBy, query, limit as firestoreLimit } from "firebase/firestore";
import { db } from "../../firebase";
import "./ego.css";
import TaEgoPersonalReport from "../report/TaEgoPersonalReport";
import { formatDuration, getDurationMs } from "../utils/formatDuration";
import { getAdminText, raporDiliSec } from "../admin/adminSozluk";
import RaporYazdirma from "../admin/RaporYazdirma";

const EGO_KEYS = ["parent", "adult", "child"];

const EGO_COLORS = {
  parent: "#f97316",
  adult: "#0ea5e9",
  child: "#22c55e",
};

// Ego adları, jenerasyon ve rol etiketleri artık admin/adminSozluk.js'te —
// iki dilli olmaları gerektiği için burada sabit duramazlardı.
// EGO_COLORS burada kalır: o bir renk haritası, çevrilecek bir şey değil.

function clampPercent(value) {
  const number = Number(value ?? 0);
  if (!Number.isFinite(number)) return 0;
  return Math.max(0, Math.min(100, Math.round(number)));
}

function getAccess(record) {
  return record?.access || record?.somerenAccess || {};
}

function getParticipant(record) {
  return record?.user || record?.participant || {};
}

function getParticipantName(record, A) {
  const user = getParticipant(record);

  const name =
    [user?.name, user?.surname].filter(Boolean).join(" ").trim() ||
    [user?.firstNameOrAlias, user?.lastName].filter(Boolean).join(" ").trim() ||
    user?.fullName ||
    user?.adSoyad ||
    user?.participantName ||
    [record?.name, record?.surname].filter(Boolean).join(" ").trim() ||
    "";

  return name || A.katilimci;
}

function getGenerationKey(record) {
  const user = getParticipant(record);
  return user?.generationRange || record?.generationRange || "";
}

function getRoleKey(record) {
  const user = getParticipant(record);
  return user?.roleLevel || record?.roleLevel || "";
}

function getGenerationLabel(value, A) {
  return A.jenerasyonlar[value] || A.belirtilmedi;
}

function getRoleLabel(value, A) {
  return A.roller[value] || A.belirtilmedi;
}

function getTimestampMillis(record) {
  const value = record?.createdAt || record?.timestamp;
  if (!value) return 0;
  if (typeof value.toDate === "function") return value.toDate().getTime();
  if (typeof value.seconds === "number") return value.seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

function formatDate(record, lang) {
  const ms = getTimestampMillis(record);
  if (!ms) return "-";
  return new Date(ms).toLocaleString(lang === "en" ? "en-GB" : "tr-TR");
}

function getScoreValues(record) {
  const summaryScores = record?.reportSummary?.scores || {};
  const scores = record?.scores || {};

  return {
    parent: clampPercent(summaryScores.parent ?? scores.parentPercent ?? scores.parent),
    adult: clampPercent(summaryScores.adult ?? scores.adultPercent ?? scores.adult),
    child: clampPercent(summaryScores.child ?? scores.childPercent ?? scores.child),
  };
}

function getDominantState(record) {
  if (record?.reportSummary?.dominantState) return record.reportSummary.dominantState;

  const scores = getScoreValues(record);
  return EGO_KEYS
    .map((key) => ({ key, value: scores[key] || 0 }))
    .sort((a, b) => b.value - a.value)[0]?.key || "adult";
}

function getDominantLabel(record, A) {
  const key = getDominantState(record);
  // Kaydın kendi etiketi Türkçe yazılmış olabilir; rapor dili İngilizce ise
  // sözlükten gelen ad kullanılır — yoksa İngilizce raporda Türkçe ego adı çıkar.
  return A.egolar[key] || record?.reportSummary?.dominantLabel || key;
}

function getBalanceType(record, A) {
  return record?.reportSummary?.balanceType || A.raporOzetiYok;
}

function countBy(records, getter) {
  return records.reduce((acc, record) => {
    const key = getter(record) || "—";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
}

function getUserForReport(record) {
  const user = getParticipant(record);

  return {
    ...user,
    name:
      user?.name ||
      user?.firstNameOrAlias ||
      user?.fullName ||
      user?.adSoyad ||
      record?.name ||
      "",
    surname: user?.surname || user?.lastName || record?.surname || "",
    generationRange: getGenerationKey(record),
    roleLevel: getRoleKey(record),
  };
}

export default function AdminPanel({ onBack, lang = "tr", setLang }) {
  const A = getAdminText(lang);
  // Rapor dili arayüz dilinden AYRI: Türkçe çalışan fasilitatör İngilizce rapor
  // basabilmeli. Varsayılan "arayuz" (bkz. admin/adminSozluk.js).
  const [raporDili, setRaporDili] = useState("arayuz");
  const [yazdirilacak, setYazdirilacak] = useState(null);
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [scopeLimit, setScopeLimit] = useState("30");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [generationFilter, setGenerationFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [sayfa, setSayfa] = useState(1);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError("");

        const resultsRef = collection(db, "egoResults");
        const snap =
          scopeLimit === "all"
            ? await getDocs(query(resultsRef, orderBy("createdAt", "desc")))
            : await getDocs(
                query(
                  resultsRef,
                  orderBy("createdAt", "desc"),
                  firestoreLimit(Number(scopeLimit))
                )
              );

        const loadedRecords = [];
        snap.forEach((docSnap) => {
          loadedRecords.push({ id: docSnap.id, ...docSnap.data() });
        });

        loadedRecords.sort((a, b) => getTimestampMillis(b) - getTimestampMillis(a));
        setRecords(loadedRecords);
      } catch (err) {
        setError(String(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [scopeLimit]);

  const companyOptions = useMemo(() => {
    const map = new Map();

    records.forEach((record) => {
      const access = getAccess(record);
      const key = access.companyId || access.companyName || "";
      const label = access.companyName || access.companyId || "";

      if (key && label) map.set(key, label);
    });

    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, "tr"));
  }, [records]);

  const filteredRecords = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const now = Date.now();

    return records.filter((record) => {
      const access = getAccess(record);
      const companyKey = access.companyId || access.companyName || "";
      const generationKey = getGenerationKey(record);
      const roleKey = getRoleKey(record);

      if (companyFilter !== "all" && companyKey !== companyFilter) return false;
      if (generationFilter !== "all" && generationKey !== generationFilter) return false;
      if (roleFilter !== "all" && roleKey !== roleFilter) return false;

      if (dateFilter !== "all") {
        const days = Number(dateFilter);
        const cutoff = now - days * 24 * 60 * 60 * 1000;
        const recordTime = getTimestampMillis(record);
        if (!recordTime || recordTime < cutoff) return false;
      }

      if (term) {
        const haystack = [
          getParticipantName(record, A),
          access.companyName,
          access.companyId,
          access.teamName,
          access.teamId,
          record.id,
          getDominantLabel(record, A),
          getBalanceType(record, A),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        if (!haystack.includes(term)) return false;
      }

      return true;
    });
  }, [records, searchTerm, companyFilter, generationFilter, roleFilter, dateFilter, A]);

  const dominantCounts = useMemo(
    () => countBy(filteredRecords, (record) => getDominantLabel(record, A)),
    [filteredRecords, A]
  );

  const generationCounts = useMemo(
    () => countBy(filteredRecords, (record) => getGenerationLabel(getGenerationKey(record), A)),
    [filteredRecords, A]
  );

  const roleCounts = useMemo(
    () => countBy(filteredRecords, (record) => getRoleLabel(getRoleKey(record), A)),
    [filteredRecords, A]
  );

  const balanceCounts = useMemo(
    () => countBy(filteredRecords, (record) => getBalanceType(record, A)),
    [filteredRecords, A]
  );

  const reportSummaryCount = filteredRecords.filter((record) => record.reportSummary).length;
  // Filtre değişince ilk sayfaya dön; yoksa 3. sayfadayken filtre daraltan
  // kişi boş tabloya bakar ve "kayıt kayboldu" sanır.
  useEffect(() => { setSayfa(1); }, [scopeLimit, companyFilter, dateFilter, generationFilter, roleFilter, searchTerm]);

  // ── Sayfalama ──
  const SAYFA_BOYU = 30;
  const toplamSayfa = Math.max(1, Math.ceil(filteredRecords.length / SAYFA_BOYU));
  // Filtre daraldığında mevcut sayfa aralık dışında kalabilir; son sayfaya çek.
  const gecerliSayfa = Math.min(sayfa, toplamSayfa);
  const ilkIndeks = (gecerliSayfa - 1) * SAYFA_BOYU;
  const latestRecords = filteredRecords.slice(ilkIndeks, ilkIndeks + SAYFA_BOYU);

  return (
    <div style={styles.page}>
      <div style={styles.topBar}>
        <h2 style={styles.title}>{A.panelBaslik}</h2>
        <div style={styles.topBarSag}>
          <DilDugmeleri lang={lang} setLang={setLang} />
          <label style={styles.dilEtiket}>
            {A.raporDili}
            <select value={raporDili} onChange={(e) => setRaporDili(e.target.value)} style={styles.dilSecici}>
              <option value="arayuz">{A.arayuzIleAyni}</option>
              <option value="kayit">{A.kaydinDili}</option>
              <option value="tr">Türkçe</option>
              <option value="en">English</option>
            </select>
          </label>
          {onBack && <button type="button" onClick={onBack} style={styles.closeButton}>{A.geri}</button>}
        </div>
      </div>

      {error && <p style={styles.error}>{A.hata}: {error}</p>}

      {loading ? (
        <p style={{ textAlign: "center", marginTop: 40 }}>{A.yukleniyor}</p>
      ) : (
        <>
          <div style={styles.kpiGrid}>
            <Kpi title={A.okunanKayit} value={records.length} />
            <Kpi title={A.filtrelenenKayit} value={filteredRecords.length} />
            <Kpi title={A.skoruOlanKayit} value={reportSummaryCount} />
            <Kpi title={A.listelenenKisi} value={latestRecords.length} />
          </div>

          <FilterPanel
            A={A}
            scopeLimit={scopeLimit}
            setScopeLimit={setScopeLimit}
            companyFilter={companyFilter}
            setCompanyFilter={setCompanyFilter}
            dateFilter={dateFilter}
            setDateFilter={setDateFilter}
            generationFilter={generationFilter}
            setGenerationFilter={setGenerationFilter}
            roleFilter={roleFilter}
            setRoleFilter={setRoleFilter}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            companyOptions={companyOptions}
            onScopeChange={() => setSelectedRecord(null)}
          />

          <div style={styles.breakdownGrid}>
            <Breakdown title={A.baskinDagilim} data={dominantCounts} A={A} />
            <Breakdown title={A.jenerasyonKirilimi} data={generationCounts} A={A} />
            <Breakdown title={A.rolKirilimi} data={roleCounts} A={A} />
            <Breakdown title={A.dengeKirilim} data={balanceCounts} A={A} />
          </div>

          <div style={styles.listeBasligi}>
            <h3 style={{ margin: 0 }}>{A.kisiListesi(filteredRecords.length)}</h3>
            {toplamSayfa > 1 && (
              <div style={styles.sayfaCubugu}>
                <span style={styles.sayfaBilgi}>
                  {A.sayfaBilgi(ilkIndeks + 1, ilkIndeks + latestRecords.length, filteredRecords.length)}
                </span>
                <button type="button" disabled={gecerliSayfa === 1}
                  style={{ ...styles.sayfaOk, ...(gecerliSayfa === 1 ? styles.sayfaPasif : null) }}
                  onClick={() => setSayfa((n) => Math.max(1, n - 1))}>‹</button>
                {Array.from({ length: toplamSayfa }, (_, i) => i + 1).map((n) => (
                  <button key={n} type="button" onClick={() => setSayfa(n)}
                    aria-current={n === gecerliSayfa ? "page" : undefined}
                    style={{ ...styles.sayfaDugme, ...(n === gecerliSayfa ? styles.sayfaAktif : null) }}>{n}</button>
                ))}
                <button type="button" disabled={gecerliSayfa === toplamSayfa}
                  style={{ ...styles.sayfaOk, ...(gecerliSayfa === toplamSayfa ? styles.sayfaPasif : null) }}
                  onClick={() => setSayfa((n) => Math.min(toplamSayfa, n + 1))}>›</button>
              </div>
            )}
          </div>
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>{A.sutunTarih}</th>
                  <th style={styles.th}>{A.sutunSure}</th>
                  <th style={styles.th}>{A.sutunKatilimci}</th>
                  <th style={styles.th}>{A.sutunSirketTakim}</th>
                  <th style={styles.th}>{A.sutunJenerasyon}</th>
                  <th style={styles.th}>{A.sutunRol}</th>
                  <th style={styles.th}>{A.sutunBaskin}</th>
                  <th style={styles.th}>{A.sutunDenge}</th>
                  <th style={styles.th}>{A.sutunIslem}</th>
                </tr>
              </thead>
              <tbody>
                {latestRecords.map((record) => {
                  const access = getAccess(record);
                  const dominantState = getDominantState(record);
                  return (
                    <tr key={record.id}>
                      <td style={styles.td}>{formatDate(record, lang)}</td>
                      <td style={styles.td}>{formatDuration(getDurationMs(record), "tr")}</td>
                      <td style={styles.td}>{getParticipantName(record, A)}</td>
                      <td style={styles.td}>
                        <strong>{access.companyName || "-"}</strong>
                        <br />
                        <small>{access.teamName || "-"}</small>
                      </td>
                      <td style={styles.td}>{getGenerationLabel(getGenerationKey(record), A)}</td>
                      <td style={styles.td}>{getRoleLabel(getRoleKey(record), A)}</td>
                      <td style={styles.td}>
                        <span style={{ ...styles.styleBadge, background: EGO_COLORS[dominantState] || "#111827" }}>
                          {getDominantLabel(record, A)}
                        </span>
                      </td>
                      <td style={styles.td}>{getBalanceType(record, A)}</td>
                      <td style={styles.td}>
                        <button style={styles.smallButton} onClick={() => setSelectedRecord(record)}>
                          Raporu Aç
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {selectedRecord && (
            <div style={styles.selectedReport}>
              <div style={styles.selectedTop}>
                <h3 style={{ margin: 0 }}>{A.secilenKisiRaporu}</h3>
                <div style={styles.topBarSag}>
                  <button style={styles.pdfButton} onClick={() => setYazdirilacak(selectedRecord)}>
                    {A.pdfYazdir}
                  </button>
                  <button style={styles.closeButton} onClick={() => setSelectedRecord(null)}>
                    {A.kapat}
                  </button>
                </div>
              </div>

              {(() => {
                // Rapor dili tek yerden; tarih de AYNI dili kullanmalı, yoksa
                // İngilizce raporun üstünde Türkçe tarih çıkıyor.
                const rDil = raporDiliSec(selectedRecord, raporDili, lang);
                return (
                  <TaEgoPersonalReport
                    scores={getScoreValues(selectedRecord)}
                    userInfo={getUserForReport(selectedRecord)}
                    timestamp={formatDate(selectedRecord, rDil)}
                    durationMs={getDurationMs(selectedRecord)}
                    language={rDil}
                    accessContext={getAccess(selectedRecord)}
                  />
                );
              })()}
            </div>
          )}
        </>
      )}

      {/* Baskı katmanı portal ile <body> altına basılır; bu ağacın içinde durmaz. */}
      {yazdirilacak && (() => {
        const rDil = raporDiliSec(yazdirilacak, raporDili, lang);
        const AR = getAdminText(rDil);
        return (
          <RaporYazdirma
            katilimciAdi={getParticipantName(yazdirilacak, AR)}
            sirket={getAccess(yazdirilacak).companyName || "-"}
            takim={getAccess(yazdirilacak).teamName || "-"}
            tarih={formatDate(yazdirilacak, rDil)}
            sureMetni={formatDuration(getDurationMs(yazdirilacak), rDil)}
            baskinAd={getDominantLabel(yazdirilacak, AR)}
            vurguRenk={EGO_COLORS[getDominantState(yazdirilacak)] || "#0ea5e9"}
            dil={rDil}
            raporProps={{
              scores: getScoreValues(yazdirilacak),
              userInfo: getUserForReport(yazdirilacak),
              timestamp: formatDate(yazdirilacak, rDil),
              durationMs: getDurationMs(yazdirilacak),
              language: rDil,
              accessContext: getAccess(yazdirilacak),
            }}
            onKapat={() => setYazdirilacak(null)}
          />
        );
      })()}
    </div>
  );
}

function DilDugmeleri({ lang, setLang }) {
  return (
    <div style={styles.dilKutu}>
      {[["tr", "TR"], ["en", "EN"]].map(([kod, etiket]) => (
        <button
          key={kod}
          type="button"
          onClick={() => setLang?.(kod)}
          aria-pressed={lang === kod}
          style={{ ...styles.dilDugme, ...(lang === kod ? styles.dilDugmeAktif : null) }}
        >
          {etiket}
        </button>
      ))}
    </div>
  );
}

function FilterPanel({
  A,
  scopeLimit,
  setScopeLimit,
  companyFilter,
  setCompanyFilter,
  dateFilter,
  setDateFilter,
  generationFilter,
  setGenerationFilter,
  roleFilter,
  setRoleFilter,
  searchTerm,
  setSearchTerm,
  companyOptions,
  onScopeChange,
}) {
  function handleScopeChange(value) {
    setScopeLimit(value);
    onScopeChange?.();
  }

  return (
    <div style={styles.filterPanel}>
      <h3 style={styles.filterTitle}>{A.filtreler}</h3>

      <div style={styles.filterGrid}>
        <label style={styles.filterLabel}>
          Kapsam
          <select
            value={scopeLimit}
            onChange={(event) => handleScopeChange(event.target.value)}
            style={styles.filterInput}
          >
            <option value="30">{A.sonNKayit(30)}</option>
            <option value="100">{A.sonNKayit(100)}</option>
            <option value="250">{A.sonNKayit(250)}</option>
            <option value="all">{A.tumKayitlar}</option>
          </select>
        </label>

        <label style={styles.filterLabel}>
          Şirket
          <select
            value={companyFilter}
            onChange={(event) => setCompanyFilter(event.target.value)}
            style={styles.filterInput}
          >
            <option value="all">{A.tumu}</option>
            {companyOptions.map((company) => (
              <option key={company.value} value={company.value}>
                {company.label}
              </option>
            ))}
          </select>
        </label>

        <label style={styles.filterLabel}>
          Tarih
          <select
            value={dateFilter}
            onChange={(event) => setDateFilter(event.target.value)}
            style={styles.filterInput}
          >
            <option value="all">{A.tumu}</option>
            <option value="7">{A.sonNGun(7)}</option>
            <option value="30">{A.sonNGun(30)}</option>
            <option value="90">{A.sonNGun(90)}</option>
          </select>
        </label>

        <label style={styles.filterLabel}>
          Jenerasyon
          <select
            value={generationFilter}
            onChange={(event) => setGenerationFilter(event.target.value)}
            style={styles.filterInput}
          >
            <option value="all">{A.tumu}</option>
            {Object.entries(A.jenerasyonlar).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
            <option value="">{A.belirtilmedi}</option>
          </select>
        </label>

        <label style={styles.filterLabel}>
          Rol seviyesi
          <select
            value={roleFilter}
            onChange={(event) => setRoleFilter(event.target.value)}
            style={styles.filterInput}
          >
            <option value="all">{A.tumu}</option>
            {Object.entries(A.roller).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
            <option value="">{A.belirtilmedi}</option>
          </select>
        </label>

        <label style={styles.filterLabel}>
          İsim / takma ad ara
          <input
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder={A.araIpucu}
            style={styles.filterInput}
          />
        </label>
      </div>
    </div>
  );
}

function Kpi({ title, value }) {
  return (
    <div style={styles.kpi}>
      <span>{title}</span>
      <strong>{value}</strong>
    </div>
  );
}

function Breakdown({ title, data, A }) {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);

  return (
    <div style={styles.card}>
      <h4 style={styles.cardTitle}>{title}</h4>
      {entries.length === 0 ? (
        <p style={styles.muted}>{A.veriYok}</p>
      ) : (
        <div style={styles.breakdownList}>
          {entries.map(([key, count]) => (
            <div key={key} style={styles.breakdownRow}>
              <span>{key}</span>
              <strong>{count}</strong>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  listeBasligi: { display: "flex", alignItems: "center", justifyContent: "space-between",
    gap: 12, flexWrap: "wrap", margin: "26px 0 10px" },
  sayfaCubugu: { display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" },
  sayfaBilgi: { fontSize: 12, color: "#6b7280", marginRight: 6, fontVariantNumeric: "tabular-nums" },
  sayfaDugme: { minWidth: 30, height: 30, borderRadius: 8, border: "1px solid #d1d5db",
    background: "#fff", color: "#374151", fontWeight: 700, fontSize: 13, cursor: "pointer",
    fontVariantNumeric: "tabular-nums" },
  sayfaAktif: { background: "#111827", borderColor: "#111827", color: "#fff" },
  sayfaOk: { width: 30, height: 30, borderRadius: 8, border: "1px solid #d1d5db",
    background: "#fff", color: "#374151", fontSize: 15, lineHeight: 1, cursor: "pointer" },
  sayfaPasif: { opacity: 0.35, cursor: "default" },

  topBar: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 8 },
  topBarSag: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
  dilKutu: { display: "inline-flex", border: "1px solid #d1d5db", borderRadius: 999, overflow: "hidden" },
  dilDugme: { border: 0, background: "#fff", color: "#374151", fontWeight: 800, fontSize: 12, padding: "6px 12px", cursor: "pointer" },
  dilDugmeAktif: { background: "#111827", color: "#fff" },
  dilEtiket: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: "#374151" },
  dilSecici: { border: "1px solid #d1d5db", borderRadius: 10, padding: "6px 8px", fontSize: 12, background: "#fff", color: "#111827" },
  pdfButton: { border: 0, borderRadius: 12, background: "#111827", color: "#fff", padding: "9px 14px", fontWeight: 800, cursor: "pointer" },
  page: {
    padding: 18,
    maxWidth: 1200,
    margin: "0 auto",
    color: "#111827",
    fontFamily: "Arial, sans-serif",
  },
  title: {
    textAlign: "center",
    marginBottom: 20,
    color: "#ffffff",
  },
  error: {
    color: "crimson",
    textAlign: "center",
  },
  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
    marginBottom: 16,
  },
  filterPanel: {
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: 18,
    padding: 16,
    marginBottom: 18,
    boxShadow: "0 8px 18px rgba(15, 23, 42, 0.06)",
  },
  filterTitle: {
    margin: "0 0 12px",
    color: "#111827",
  },
  filterGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
    gap: 12,
  },
  filterLabel: {
    display: "grid",
    gap: 6,
    fontSize: 13,
    fontWeight: 800,
    color: "#111827",
  },
  filterInput: {
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid #cbd5e1",
    borderRadius: 10,
    padding: "9px 10px",
    background: "#ffffff",
    color: "#111827",
  },
  kpi: {
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: 16,
    padding: 16,
    display: "grid",
    gap: 6,
    boxShadow: "0 8px 18px rgba(15, 23, 42, 0.06)",
  },
  breakdownGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 12,
    marginBottom: 24,
  },
  card: {
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: 16,
    padding: 16,
    boxShadow: "0 8px 18px rgba(15, 23, 42, 0.06)",
  },
  cardTitle: {
    margin: "0 0 10px",
    color: "#111827",
  },
  muted: {
    color: "#6b7280",
    margin: 0,
  },
  breakdownList: {
    display: "grid",
    gap: 8,
  },
  breakdownRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    borderBottom: "1px solid #f1f5f9",
    paddingBottom: 6,
    fontSize: 14,
    color: "#111827",
  },
  tableWrap: {
    overflowX: "auto",
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: 16,
    marginBottom: 24,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
    color: "#111827",
  },
  th: {
    textAlign: "left",
    padding: "10px 12px",
    borderBottom: "1px solid #e5e7eb",
    background: "#f8fafc",
    whiteSpace: "nowrap",
    color: "#111827",
  },
  td: {
    padding: "10px 12px",
    borderBottom: "1px solid #f1f5f9",
    verticalAlign: "top",
    color: "#111827",
  },
  styleBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 28,
    borderRadius: 999,
    color: "#ffffff",
    fontWeight: 900,
    padding: "5px 10px",
    whiteSpace: "nowrap",
  },
  smallButton: {
    border: "0",
    borderRadius: 10,
    background: "#111827",
    color: "#ffffff",
    padding: "8px 10px",
    cursor: "pointer",
    fontWeight: 800,
    whiteSpace: "nowrap",
  },
  selectedReport: {
    borderTop: "2px solid #e5e7eb",
    marginTop: 28,
    paddingTop: 18,
  },
  selectedTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    marginBottom: 8,
    color: "#ffffff",
  },
  closeButton: {
    border: "1px solid #d1d5db",
    borderRadius: 10,
    background: "#ffffff",
    color: "#111827",
    padding: "8px 12px",
    cursor: "pointer",
    fontWeight: 800,
  },
};
